import compositionApi, { defineComponent, inject, ref, onMounted, getCurrentInstance, onUnmounted, watch, provide } from '@vue/composition-api';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import Deferred from 'my-deferred/dist/src';
import mapboxgl, { Marker, GeoJSONSource } from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import Deferred$1 from 'my-deferred';
import deepEqual from 'fast-deep-equal';
import 'mapbox-gl/dist/mapbox-gl.css';

const slotIsNotEmpty = (el) => el && el.outerHTML !== '<div></div>';
const filterObject = (object, keys, blacklist = [undefined]) => {
    const result = {};
    const _keys = keys || Object.keys(object);
    for (const key of _keys) {
        if (!blacklist.some(b => b === object[key])) {
            result[key] = object[key];
        }
    }
    return result;
};
const parentsNameIs = (instance, parentName) => {
    if (!instance.parent)
        return false;
    // Vue 3
    if (instance.parent.type)
        return instance.parent.type.name === parentName;
    // Vue 2
    if (instance.parent.vnode.tag)
        return instance.parent.vnode.tag.endsWith(parentName);
    return false;
};
const parentNameContains = (instance, parentNameFragment) => {
    if (!instance.parent)
        return false;
    // Vue 3
    if (instance.parent.type)
        return instance.parent.type.name.includes(parentNameFragment);
    // Vue 2
    if (instance.parent.vnode.tag)
        return instance.parent.vnode.tag.includes(parentNameFragment);
    return false;
};
const duplicateEvents = (object, instance, events) => {
    for (const event of events)
        object.on(event, (evt) => {
            instance.emit(event, evt);
        });
};

const mountGeocoderControl = async (vmb_map, vmb_geocoder, props, instance) => {
    const map = vmb_map ? await vmb_map.promise : null;
    const options = getGeocoderControlOptions(props);
    const geocoder = new MapboxGeocoder(options);
    if (parentsNameIs(instance, 'MapboxMap') && map)
        map.addControl(geocoder);
    else {
        if (instance.parent)
            geocoder.addTo(instance.refs.geocoder);
        if (slotIsNotEmpty(instance.refs['custom-input'])) {
            attachToInput(instance.refs['custom-input'], geocoder, instance);
        }
    }
    window.MapboxGeocoder = MapboxGeocoder;
    window.geocoder = geocoder;
    vmb_geocoder.resolve(geocoder);
};
const mountGeocoderEvents = async (vmb_geocoder, instance) => {
    const geocoder = await vmb_geocoder.promise;
    geocoder.on('result', (res) => {
        instance.emit('result', res.result);
        instance.proxy.geocoderState.result = res.result;
    });
    geocoder.on('results', (res) => {
        instance.emit('results', res);
        instance.proxy.geocoderState.results = res;
    });
    geocoder.on('error', (err) => {
        instance.emit('error', err);
        instance.proxy.geocoderState.error = err;
    });
    geocoder.on('loading', (query) => {
        instance.emit('loading', query);
        instance.proxy.geocoderState.loading = query;
    });
};
const getGeocoderControlOptions = (props) => {
    const options = filterObject(props);
    options.accessToken = props.accessToken || mapboxgl.accessToken;
    options.mapboxgl = mapboxgl;
    return options;
};
const attachToInput = (ref, geocoder, instance) => {
    if (instance && instance.proxy) {
        if (instance.proxy.showOriginalGeocoder)
            instance.proxy.showOriginalGeocoder = false;
        // instance.setupState.showOriginalGeocoder = false;
        let inputs = ref.getElementsByTagName('input');
        if (!inputs)
            throw new Error('MapboxGeocoderControl: No inputs found');
        const input = inputs[0];
        input.addEventListener('keyup', (evt) => {
            geocoder.query(input.value);
        });
    }
};

var script$f = defineComponent({
    name: 'MapboxGeocoderControl',
    props: {
        accessToken: { type: String, default: '' },
        origin: { type: String },
        zoom: { type: Number },
        flyTo: { type: [Boolean, Object], default: () => undefined },
        placeholder: { type: String },
        proximity: { type: Object },
        trackProximity: { type: Boolean, default: () => undefined },
        collapsed: { type: Boolean, default: () => undefined },
        clearAndBlurOnEsc: { type: Boolean, default: () => undefined },
        clearOnBlur: { type: Boolean, default: () => undefined },
        bbox: { type: Array },
        countries: { type: String },
        types: { type: String },
        minLength: { type: Number },
        limit: { type: Number },
        language: { type: String },
        filter: { type: Function },
        localGeocoder: { type: Function },
        externalGeocoder: { type: Function },
        // reverseMode:        { type: String as () => 'distance' | 'score' },
        reverseGeocode: { type: Boolean, default: () => undefined },
        enableEventLogging: { type: Boolean, default: () => undefined },
        marker: { type: Boolean, default: () => undefined },
        render: { type: Function },
        getItemValue: { type: Function },
        mode: { type: String },
        localGeocoderOnly: { type: Boolean, default: () => undefined }
    },
    setup(props) {
        const vmb_map = inject('vmb_map', null);
        const vmb_geocoder = new Deferred();
        const showOriginalGeocoder = ref(true);
        const geocoderState = ref({
            result: {},
            results: {},
            error: null,
            loading: null,
        });
        onMounted(async () => {
            const instance = getCurrentInstance();
            if (instance)
                await mountGeocoderControl(vmb_map, vmb_geocoder, props, instance);
            if (instance)
                await mountGeocoderEvents(vmb_geocoder, instance);
        });
        onUnmounted(async () => {
            const geocoder = await vmb_geocoder.promise;
            if (vmb_map) {
                const map = await vmb_map.promise;
                if (!map._removed)
                    map.removeControl(geocoder);
            }
        });
        watch(props, async () => {
        });
        return { showOriginalGeocoder, geocoderState };
    }
});

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

/* script */
const __vue_script__$f = script$f;

/* template */
var __vue_render__$f = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[_c('div',{directives:[{name:"show",rawName:"v-show",value:(_vm.showOriginalGeocoder),expression:"showOriginalGeocoder"}],ref:"geocoder"}),_vm._v(" "),_c('div',{ref:"custom-input"},[_vm._t("input",null,{"result":_vm.geocoderState.result,"results":_vm.geocoderState.results,"error":_vm.geocoderState.error,"loading":_vm.geocoderState.loading})],2)])};
var __vue_staticRenderFns__$f = [];

  /* style */
  const __vue_inject_styles__$f = undefined;
  /* scoped */
  const __vue_scope_id__$f = undefined;
  /* module identifier */
  const __vue_module_identifier__$f = undefined;
  /* functional template */
  const __vue_is_functional_template__$f = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$f = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$f, staticRenderFns: __vue_staticRenderFns__$f },
    __vue_inject_styles__$f,
    __vue_script__$f,
    __vue_scope_id__$f,
    __vue_is_functional_template__$f,
    __vue_module_identifier__$f,
    false,
    undefined,
    undefined,
    undefined
  );

const getStyle = (props) => ({
    height: props.height,
    width: props.width,
    '--zoom-logo': props.zoomLogo >= 0.8 ? props.zoomLogo : 1,
});
const getMapStyle = (raw) => raw.includes('/') ? raw : `mapbox://styles/mapbox/${raw}`;
const getMapboxOptions = (props, el) => {
    const opts = filterObject(props, [
        'accessToken',
        'antialias',
        'attributionControl',
        'bearing',
        'bearingSnap',
        'bounds',
        'boxZoom',
        'center',
        'clickTolerance',
        'collectResourceTiming',
        'container',
        'crossSourceCollisions',
        'customAttribution',
        'doubleClickZoom',
        'dragPan',
        'dragRotate',
        'fadeDuration',
        'failIfMajorPerformanceCaveat',
        'fitBoundsOptions',
        'hash',
        'interactive',
        'keyboard',
        'localIdeographFontFamily',
        'logoPosition',
        'maxBounds',
        'maxPitch',
        'maxTileCacheSize',
        'maxZoom',
        'minPitch',
        'minZoom',
        'pitch',
        'pitchWithRotate',
        'preserveDrawingBuffer',
        'refreshExpiredTiles',
        'renderWorldCopies',
        'scrollZoom',
        'touchZoomRotate',
        'trackResize',
        'transformRequest',
        'zoom',
    ]);
    opts.style = getMapStyle(props.mapStyle);
    opts.container = el;
    return opts;
};
const mountMap = (props, vmb_map, rootRef) => (() => {
    const element = rootRef.value;
    const mapOptions = getMapboxOptions(props, element);
    const map = new mapboxgl.Map(mapOptions);
    map.on('load', () => {
        vmb_map.resolve(map);
    });
})();
const coordsChanged = (newCoords, oldCorrds) => newCoords[0] !== oldCorrds[0] || newCoords[1] !== newCoords[1];
const updateStyle = (props, style) => {
    style.value = getStyle(props);
};
const updateMap = async (vmb_map, props, rootRef) => {
    const map = await vmb_map.promise;
    const element = rootRef.value;
    const opts = getMapboxOptions(props, element);
    const flyToOptions = filterObject(props.flyToOptions, ['curve', 'maxDuration', 'minZoom', 'screenSpeed', 'speed']);
    const dimentionOptions = filterObject(props, ['width', 'height']);
    let positionUpdated = false;
    if (dimentionOptions.width || dimentionOptions.height)
        map.resize();
    if (typeof opts.bearing === 'number')
        map.setBearing(opts.bearing);
    if (opts.maxBounds)
        map.setMaxBounds(opts.maxBounds);
    if (typeof opts.maxPitch === 'number')
        map.setMaxPitch(opts.maxPitch);
    if (typeof opts.maxZoom === 'number')
        map.setMaxZoom(opts.maxZoom);
    if (typeof opts.minPitch === 'number')
        map.setMinPitch(opts.minPitch);
    if (typeof opts.pitch === 'number')
        map.setPitch(opts.pitch);
    if (typeof opts.renderWorldCopies === 'boolean')
        map.setRenderWorldCopies(opts.renderWorldCopies);
    if (opts.center && !positionUpdated) {
        const newCenter = opts.center;
        const currentCenter = map.getCenter().toArray();
        if (coordsChanged(newCenter, currentCenter)) {
            map.flyTo({
                center: opts.center,
                zoom: opts.zoom,
                ...flyToOptions
            });
            positionUpdated = true;
        }
    }
    if (typeof opts.zoom === 'number' && !positionUpdated)
        if (map.getZoom() !== opts.zoom && !positionUpdated) {
            map.flyTo({
                center: opts.center,
                zoom: opts.zoom,
                ...flyToOptions
            });
            positionUpdated = true;
        }
};
const MapGlEvents = [
    'boxzoomstart',
    'click',
    'contextmenu',
    'data',
    'dataloading',
    'dbclick',
    'drag',
    'dragend',
    'dragstart',
    'error',
    'idle',
    'load',
    'mousedown',
    'mouseenter',
    'mouseleave',
    'mousemouve',
    'mouseout',
    'mouseover',
    'mouseup',
    'move',
    'moveend',
    'movestart',
    'pitch',
    'pitchend',
    'pitchstart',
    'remove',
    'render',
    'resize',
    'rotate',
    'rotateend',
    'rotatestart',
    'sourcedata',
    'sourcedataloading',
    'styledata',
    'styledataloading',
    'styleimagemissing',
    'touchcancel',
    'touchend',
    'touchstart',
    'webglcontextlost',
    'webglcontextrestored',
    'wheel',
    'zoom',
    'zoomend',
    'zoomstart'
];
const MapEmits = [
    ...MapGlEvents,
    'update:center',
    'update:zoom',
    'update:pitch',
];
const registerMapEvents = async (vmb_map, instance) => {
    const map = await vmb_map.promise;
    duplicateEvents(map, instance, MapGlEvents);
    map.on('zoomend', evt => {
        instance.emit('update:zoom', evt.target.getZoom());
    });
    map.on('dragend', evt => {
        instance.emit('update:center', evt.target.getCenter().toArray());
    });
    map.on('pitchend', evt => {
        instance.emit('update:pitch', evt.target.getPitch());
    });
};

var script$e = defineComponent({
    name: 'MapboxMap',
    emits: MapEmits,
    props: {
        accessToken: {
            type: String,
            default: undefined
        },
        height: {
            type: String,
            default: '500px'
        },
        width: {
            type: String,
            default: '100%'
        },
        container: {
            type: [Object, String] || String,
            default: ''
        },
        minZoom: {
            type: Number,
            default: 0,
        },
        maxZoom: {
            type: Number,
            default: 22,
        },
        minPitch: {
            type: Number,
            default: 0,
        },
        maxPitch: {
            type: Number,
            default: 60,
        },
        mapStyle: {
            type: [Object, String],
            default: 'mapbox://styles/mapbox/streets-v11'
        },
        hash: {
            type: Boolean,
            default: false,
        },
        interactive: {
            type: Boolean,
            default: true,
        },
        bearingSnap: {
            type: Number,
            default: 7,
        },
        pitchWithRotate: {
            type: Boolean,
            default: true,
        },
        clickTolerance: {
            type: Number,
            default: 3,
        },
        attributionControl: {
            type: Boolean,
            default: true,
        },
        customAttribution: {
            type: Array,
            default: null,
        },
        logoPosition: {
            type: String,
            default: 'bottom-left',
        },
        failIfMajorPerformanceCaveat: {
            type: Boolean,
            default: false,
        },
        preserveDrawingBuffer: {
            type: Boolean,
            default: false,
        },
        antialias: {
            type: Boolean,
            default: false,
        },
        refreshExpiredTiles: {
            type: Boolean,
            default: true,
        },
        maxBounds: {
            type: Array,
            default: undefined,
        },
        scrollZoom: {
            type: [Boolean, Object],
            default: true,
        },
        boxZoom: {
            type: Boolean,
            default: true,
        },
        dragRotate: {
            type: Boolean,
            default: true,
        },
        dragPan: {
            type: [Object, Boolean],
            default: true,
        },
        keyboard: {
            type: Boolean,
            default: true,
        },
        doubleClickZoom: {
            type: Boolean,
            default: true,
        },
        touchZoomRotate: {
            type: [Boolean, Object],
            default: true,
        },
        trackResize: {
            type: Boolean,
            default: true,
        },
        center: {
            default: () => [0, 0],
        },
        zoom: {
            type: Number,
            default: 0,
        },
        bearing: {
            type: Number,
            default: 0,
        },
        pitch: {
            type: Number,
            default: 0,
        },
        bounds: {
            type: Array,
            default: undefined,
        },
        fitBoundsOptions: {
            type: Object,
            default: null,
        },
        renderWorldCopies: {
            type: Boolean,
            default: true,
        },
        maxTileCacheSize: {
            type: Number,
            default: null,
        },
        localIdeographFontFamily: {
            type: String,
            default: 'sans-serif',
        },
        transformRequest: {
            type: Function,
            default: null,
        },
        collectResourceTiming: {
            type: Boolean,
            default: false,
        },
        fadeDuration: {
            type: Number,
            default: 300,
        },
        crossSourceCollisions: {
            type: Boolean,
            default: true,
        },
        zoomLogo: {
            type: Number,
            default: 1
        },
        flyToOptions: {
            default: () => ({})
        }
    },
    setup: (props) => {
        const root = ref(null);
        const vmb_map = new Deferred$1();
        provide('vmb_map', vmb_map);
        const style = ref(getStyle(props));
        onMounted(async () => {
            const instance = getCurrentInstance();
            mapboxgl.accessToken = props.accessToken;
            mountMap(props, vmb_map, root);
            if (instance)
                await registerMapEvents(vmb_map, instance);
        });
        watch(props, async (p) => {
            console.log('UPDATED MAP');
            updateMap(vmb_map, p, root);
            updateStyle(p, style);
        });
        onUnmounted(async () => {
            const map = await vmb_map.promise;
            map.remove();
        });
        return {
            vmb_map, root, style
        };
    },
});

const isOldIE = typeof navigator !== 'undefined' &&
    /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());
function createInjector(context) {
    return (id, style) => addStyle(id, style);
}
let HEAD;
const styles = {};
function addStyle(id, css) {
    const group = isOldIE ? css.media || 'default' : id;
    const style = styles[group] || (styles[group] = { ids: new Set(), styles: [] });
    if (!style.ids.has(id)) {
        style.ids.add(id);
        let code = css.source;
        if (css.map) {
            // https://developer.chrome.com/devtools/docs/javascript-debugging
            // this makes source maps inside style tags work properly in Chrome
            code += '\n/*# sourceURL=' + css.map.sources[0] + ' */';
            // http://stackoverflow.com/a/26603875
            code +=
                '\n/*# sourceMappingURL=data:application/json;base64,' +
                    btoa(unescape(encodeURIComponent(JSON.stringify(css.map)))) +
                    ' */';
        }
        if (!style.element) {
            style.element = document.createElement('style');
            style.element.type = 'text/css';
            if (css.media)
                style.element.setAttribute('media', css.media);
            if (HEAD === undefined) {
                HEAD = document.head || document.getElementsByTagName('head')[0];
            }
            HEAD.appendChild(style.element);
        }
        if ('styleSheet' in style.element) {
            style.styles.push(code);
            style.element.styleSheet.cssText = style.styles
                .filter(Boolean)
                .join('\n');
        }
        else {
            const index = style.ids.size - 1;
            const textNode = document.createTextNode(code);
            const nodes = style.element.childNodes;
            if (nodes[index])
                style.element.removeChild(nodes[index]);
            if (nodes.length)
                style.element.insertBefore(textNode, nodes[index]);
            else
                style.element.appendChild(textNode);
        }
    }
}

/* script */
const __vue_script__$e = script$e;

/* template */
var __vue_render__$e = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{style:(_vm.style)},[(_vm.vmb_map.isResolved)?_c('div',{ref:"root",style:({height: '100%', width: '100%' })},[_vm._t("default")],2):_c('div',[_vm._t("loader",[_c('div',{style:(_vm.style)})])],2)])};
var __vue_staticRenderFns__$e = [];

  /* style */
  const __vue_inject_styles__$e = function (inject) {
    if (!inject) return
    inject("data-v-870efa00_0", { source: "[data-v-870efa00]:deep(.mapboxgl-ctrl-logo){zoom:var(--zoom-logo)}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$e = "data-v-870efa00";
  /* module identifier */
  const __vue_module_identifier__$e = undefined;
  /* functional template */
  const __vue_is_functional_template__$e = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$e = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$e, staticRenderFns: __vue_staticRenderFns__$e },
    __vue_inject_styles__$e,
    __vue_script__$e,
    __vue_scope_id__$e,
    __vue_is_functional_template__$e,
    __vue_module_identifier__$e,
    false,
    createInjector,
    undefined,
    undefined
  );

var injectMap = {
    setup() {
        const vmb_map = inject('vmb_map', null);
        return { vmb_map };
    },
};

const parentIsMarker = (instance) => parentsNameIs(instance, 'MapboxMarker');
const getMarkerOptions = (props) => filterObject(props, [
    'element',
    'offset',
    'anchor',
    'color',
    'draggable',
    'clickTolerance',
    'rotation',
    'rotationAlignment',
    'pitchAlignment',
    'scale'
]);
const MarkerGlEvents = ['drag', 'dragend', 'dragstart'];
const MarkerEmits = [...MarkerGlEvents, 'update:lngLat', 'click'];
const registerMarkerEvents = (marker, component) => {
    duplicateEvents(marker, component, MarkerGlEvents);
    marker.on('dragend', (evt) => component.emit('update:lngLat', evt.target._lngLat.toArray()));
    marker.getElement().addEventListener('click', _ev => {
        component.emit('click', marker);
    });
};
const updateMarker = async (props, vmb_marker) => {
    const marker = await vmb_marker.promise;
    const opts = filterObject(props);
    if (typeof opts.draggable === 'boolean')
        marker.setDraggable(opts.draggable);
    if (opts.lngLat)
        marker.setLngLat(opts.lngLat);
    if (opts.offset)
        marker.setOffset(opts.offset);
    if (opts.rotation)
        marker.setRotation(opts.rotation);
    if (opts.pitchAlignment)
        marker.setPitchAlignment(opts.pitchAlignment);
    if (opts.rotationAlignment)
        marker.setRotationAlignment(opts.rotationAlignment);
};
const mountMarker = async (options, vmb_map, vmb_marker, instance, lngLat, element) => {
    const map = await vmb_map.promise;
    if (element && slotIsNotEmpty(element.value))
        options.element = element.value;
    const marker = new Marker(options)
        .setLngLat(lngLat);
    marker.addTo(map);
    vmb_marker.resolve(marker);
};

var script$d = defineComponent({
    name: 'MapboxMarker',
    emits: MarkerEmits,
    props: {
        lngLat: {
            default: () => [0, 0],
        },
        offset: {
            type: [Array],
            default: () => [0, 0],
        },
        anchor: {
            type: String,
            default: () => 'left',
        },
        color: {
            type: String,
            default: '#4668F2',
        },
        scale: {
            type: Number,
            default: 1,
        },
        draggable: {
            type: Boolean,
            default: false,
        },
        rotation: {
            type: Number,
            default: 0,
        },
        pitchAlignment: {
            type: String,
            default: 'auto',
        },
        rotationAlignment: {
            type: String,
            default: 'auto',
        },
    },
    setup(props) {
        const { vmb_map } = injectMap.setup();
        const vmb_marker = new Deferred();
        const icon = ref(null);
        const options = getMarkerOptions(props);
        provide('vmb_marker', vmb_marker);
        onMounted(async () => {
            const instance = getCurrentInstance();
            if (instance && vmb_map) {
                await mountMarker(options, vmb_map, vmb_marker, instance, props.lngLat, icon);
                const marker = await vmb_marker.promise;
                registerMarkerEvents(marker, instance);
            }
        });
        onUnmounted(async () => {
            const marker = await vmb_marker.promise;
            marker.remove();
        });
        watch(props, async (p) => {
            await updateMarker(p, vmb_marker);
        });
        return { icon };
    },
});

/* script */
const __vue_script__$d = script$d;

/* template */
var __vue_render__$d = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[_c('div',{ref:"icon"},[_vm._t("icon",[_c('svg',{attrs:{"display":"block","height":42*_vm.scale+'px',"width":27*_vm.scale+'px',"viewBox":"0 0 27 41"}},[_c('g',{attrs:{"fill-rule":"nonzero"}},[_c('g',{attrs:{"transform":"translate(3.0, 29.0)","fill":"#000000"}},[_c('ellipse',{attrs:{"opacity":"0.04","cx":"10.5","cy":"5.80029008","rx":"10.5","ry":"5.25002273"}}),_vm._v(" "),_c('ellipse',{attrs:{"opacity":"0.04","cx":"10.5","cy":"5.80029008","rx":"10.5","ry":"5.25002273"}}),_vm._v(" "),_c('ellipse',{attrs:{"opacity":"0.04","cx":"10.5","cy":"5.80029008","rx":"9.5","ry":"4.77275007"}}),_vm._v(" "),_c('ellipse',{attrs:{"opacity":"0.04","cx":"10.5","cy":"5.80029008","rx":"8.5","ry":"4.29549936"}}),_vm._v(" "),_c('ellipse',{attrs:{"opacity":"0.04","cx":"10.5","cy":"5.80029008","rx":"7.5","ry":"3.81822308"}}),_vm._v(" "),_c('ellipse',{attrs:{"opacity":"0.04","cx":"10.5","cy":"5.80029008","rx":"6.5","ry":"3.34094679"}}),_vm._v(" "),_c('ellipse',{attrs:{"opacity":"0.04","cx":"10.5","cy":"5.80029008","rx":"5.5","ry":"2.86367051"}}),_vm._v(" "),_c('ellipse',{attrs:{"opacity":"0.04","cx":"10.5","cy":"5.80029008","rx":"4.5","ry":"2.38636864"}})]),_vm._v(" "),_c('g',{attrs:{"fill":_vm.color}},[_c('path',{attrs:{"d":"M27,13.5 C27,19.074644 20.250001,27.000002 14.75,34.500002 C14.016665,35.500004 12.983335,35.500004 12.25,34.500002 C6.7499993,27.000002 0,19.222562 0,13.5 C0,6.0441559 6.0441559,0 13.5,0 C20.955844,0 27,6.0441559 27,13.5 Z"}})]),_vm._v(" "),_c('g',{attrs:{"opacity":"0.25","fill":"#000000"}},[_c('path',{attrs:{"d":"M13.5,0 C6.0441559,0 0,6.0441559 0,13.5 C0,19.222562 6.7499993,27 12.25,34.5 C13,35.522727 14.016664,35.500004 14.75,34.5 C20.250001,27 27,19.074644 27,13.5 C27,6.0441559 20.955844,0 13.5,0 Z M13.5,1 C20.415404,1 26,6.584596 26,13.5 C26,15.898657 24.495584,19.181431 22.220703,22.738281 C19.945823,26.295132 16.705119,30.142167 13.943359,33.908203 C13.743445,34.180814 13.612715,34.322738 13.5,34.441406 C13.387285,34.322738 13.256555,34.180814 13.056641,33.908203 C10.284481,30.127985 7.4148684,26.314159 5.015625,22.773438 C2.6163816,19.232715 1,15.953538 1,13.5 C1,6.584596 6.584596,1 13.5,1 Z"}})]),_vm._v(" "),_c('g',{attrs:{"transform":"translate(6.0, 7.0)","fill":"#FFFFFF"}}),_c('g',{attrs:{"transform":"translate(8.0, 8.0)"}},[_c('circle',{attrs:{"fill":"#000000","opacity":"0.25","cx":"5.5","cy":"5.5","r":"5.4999962"}}),_vm._v(" "),_c('circle',{attrs:{"fill":"#FFFFFF","cx":"5.5","cy":"5.5","r":"5.4999962"}})])])])])],2),_vm._v(" "),_vm._t("default")],2)};
var __vue_staticRenderFns__$d = [];

  /* style */
  const __vue_inject_styles__$d = undefined;
  /* scoped */
  const __vue_scope_id__$d = undefined;
  /* module identifier */
  const __vue_module_identifier__$d = undefined;
  /* functional template */
  const __vue_is_functional_template__$d = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$d = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$d, staticRenderFns: __vue_staticRenderFns__$d },
    __vue_inject_styles__$d,
    __vue_script__$d,
    __vue_scope_id__$d,
    __vue_is_functional_template__$d,
    __vue_module_identifier__$d,
    false,
    undefined,
    undefined,
    undefined
  );

const getNavigationControlOptions = (props) => {
    const { showCompass, showZoom, vizualizePitch } = props;
    return {
        showCompass,
        showZoom,
        vizualizePitch
    };
};
const mountNavigationControl = (nav, vmb_map, position) => (async () => {
    const map = await vmb_map.promise;
    map.addControl(nav, position);
})();

var script$c = defineComponent({
    name: 'MapboxNavigationControl',
    props: {
        showCompass: {
            type: Boolean,
            default: true
        },
        showZoom: {
            type: Boolean,
            default: true
        },
        visualizePitch: {
            type: Boolean,
            default: false
        },
        position: {
            type: String,
            default: () => 'top-right'
        }
    },
    setup(props) {
        const vmb_map = inject('vmb_map', null);
        const navOptions = getNavigationControlOptions(props);
        const vmb_navigationControl = new mapboxgl.NavigationControl(navOptions);
        const position = props.position;
        onMounted(() => {
            if (vmb_map)
                mountNavigationControl(vmb_navigationControl, vmb_map, position);
        });
        onUnmounted(async () => {
            if (vmb_map) {
                const map = await vmb_map.promise;
                map.removeControl(vmb_navigationControl);
            }
        });
    }
});

/* script */
const __vue_script__$c = script$c;

/* template */
var __vue_render__$c = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div')};
var __vue_staticRenderFns__$c = [];

  /* style */
  const __vue_inject_styles__$c = undefined;
  /* scoped */
  const __vue_scope_id__$c = undefined;
  /* module identifier */
  const __vue_module_identifier__$c = undefined;
  /* functional template */
  const __vue_is_functional_template__$c = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$c = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$c, staticRenderFns: __vue_staticRenderFns__$c },
    __vue_inject_styles__$c,
    __vue_script__$c,
    __vue_scope_id__$c,
    __vue_is_functional_template__$c,
    __vue_module_identifier__$c,
    false,
    undefined,
    undefined,
    undefined
  );

const removeLayerIfPresent = (id, map) => {
    const layer = map.getLayer(id);
    if (layer)
        map.removeLayer(id);
};

const removeSourceIfPresent = (id, map) => {
    const source = map.getSource(id);
    if (source)
        map.removeSource(id);
};

const parentIsGeogeometry = (instance) => parentNameContains(instance, 'MapboxGeogeometry');
const distanceToLat = (km) => km / 110.574;
const distanceToLong = (km, lat) => km / (Math.cos(lat * Math.PI / 180) * 111.32);
const mountGeogeometry = async (vmb_map, vmb_geogeometry) => {
    const map = await vmb_map.promise;
    const geogeometry = vmb_geogeometry;
    removeLayerIfPresent(geogeometry.id, map);
    removeSourceIfPresent(geogeometry.id, map);
    map.addSource(geogeometry.id, geogeometry.getGeoJSON());
    // map.addLayer(geogeometry.getLayer());
};
const updateGeogeometry = async (props, vmb_map, vmb_geogeometry) => {
    const map = await vmb_map.promise;
    const geo = vmb_geogeometry;
    const opts = filterObject(props);
    const source = map.getSource(geo.id);
    geo.updateOptions(opts);
    source.setData(geo.getGeoJSON().data);
};

const attachToMarker = async (instance, vmb_marker, popup) => {
    if (vmb_marker) {
        const marker = await vmb_marker.promise;
        marker.setPopup(popup);
    }
};
const attachToGeogeometry = async (vmb_map, vmb_geo_paint, popup) => {
    const paint = await vmb_geo_paint.promise;
    const map = await vmb_map.promise;
    if (paint.id) {
        const geo = await paint.geogeometry.promise;
        map.on('click', paint.id, e => {
            const coordinates = geo.center;
            popup
                .setLngLat(coordinates)
                .addTo(map);
        });
    }
};
const getPopupOptions = (props) => filterObject(props, [
    'closeButton',
    'closeOnClick',
    'closeOnMove',
    'focusAfterOpen',
    'anchor',
    'offset',
    'className',
    'maxWidth'
]);
const mountPopup = async (
// instance: any | null,
instance, vmb_map, vmb_popup, vmb_marker, vmb_geogeometry_paint, content) => {
    const map = await vmb_map.promise;
    const popup = vmb_popup;
    popup.setDOMContent(content.value);
    if (parentIsMarker(instance))
        await attachToMarker(instance, vmb_marker, popup);
    else if (parentIsGeogeometry(instance) && vmb_geogeometry_paint) {
        await attachToGeogeometry(vmb_map, vmb_geogeometry_paint, popup);
    }
    else {
        popup.addTo(map);
    }
};
const PopupGlEvents = ['close', 'open'];
const PopupEmits = [...PopupGlEvents];
const registerPopupEvents = (vmb_popup, instance) => {
    duplicateEvents(vmb_popup, instance, PopupGlEvents);
};
const updatePopup = async (props, vmb_popup) => {
    const opts = getPopupOptions(props);
    if (opts.maxWidth)
        vmb_popup.setMaxWidth(opts.maxWidth);
    if (opts.offset)
        vmb_popup.setOffset(opts.offset);
    if (props.lngLat)
        vmb_popup.setLngLat(props.lngLat);
};

var script$b = defineComponent({
    name: 'MapboxPopup',
    emits: PopupEmits,
    props: {
        lngLat: {
            default: () => [0, 0],
        },
        closeButton: {
            type: Boolean,
            default: false
        },
        closeOnClick: {
            type: Boolean,
            default: true
        },
        closeOnMove: {
            type: Boolean,
            default: false
        },
        anchor: {
            type: String,
            default: undefined
        },
        offset: {
            type: Number,
            default: undefined
        },
        className: {
            type: String
        },
        maxWidth: {
            type: String,
            default: '500px'
        },
        renderless: {
            type: Boolean,
            default: false,
        }
    },
    setup(props) {
        const content = ref(null);
        const vmb_map = inject('vmb_map', null);
        const vmb_marker = inject('vmb_marker', null);
        const vmb_paint = inject('vmb_geogeometry_paint', null);
        const popupOptions = getPopupOptions(props);
        const vmb_popup = new mapboxgl.Popup(popupOptions)
            .setLngLat(props.lngLat);
        onMounted(async () => {
            const instance = getCurrentInstance();
            if (vmb_map && instance) {
                await mountPopup(instance, vmb_map, vmb_popup, vmb_marker, vmb_paint, content);
                registerPopupEvents(vmb_popup, instance);
            }
        });
        watch(props, props => {
            updatePopup(props, vmb_popup);
        });
        onUnmounted(async () => {
            vmb_popup.remove();
        });
        return { content };
    }
});

/* script */
const __vue_script__$b = script$b;

/* template */
var __vue_render__$b = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{ref:"content"},[_vm._t("default")],2)};
var __vue_staticRenderFns__$b = [];

  /* style */
  const __vue_inject_styles__$b = undefined;
  /* scoped */
  const __vue_scope_id__$b = undefined;
  /* module identifier */
  const __vue_module_identifier__$b = undefined;
  /* functional template */
  const __vue_is_functional_template__$b = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$b = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$b, staticRenderFns: __vue_staticRenderFns__$b },
    __vue_inject_styles__$b,
    __vue_script__$b,
    __vue_scope_id__$b,
    __vue_is_functional_template__$b,
    __vue_module_identifier__$b,
    false,
    undefined,
    undefined,
    undefined
  );

const getAttributionControlOptions = (props) => filterObject(props);
const mountAttributionControl = async (vmb_map, vmb_attributionControl) => {
    const map = await vmb_map.promise;
    map.addControl(vmb_attributionControl);
};

var script$a = defineComponent({
    name: 'MapboxAttributionControl',
    props: {
        compact: {
            type: Boolean,
            default: false
        },
        customAttribution: {
            type: [String, Array],
            default: undefined
        }
    },
    setup(props) {
        const opts = getAttributionControlOptions(props);
        const vmb_attributionControl = new mapboxgl.AttributionControl(opts);
        const vmb_map = inject('vmb_map', null);
        onMounted(async () => {
            if (vmb_map)
                mountAttributionControl(vmb_map, vmb_attributionControl);
        });
        onUnmounted(async () => {
            if (vmb_map) {
                const map = await vmb_map.promise;
                map.removeControl(vmb_attributionControl);
            }
        });
    }
});

/* script */
const __vue_script__$a = script$a;

/* template */
var __vue_render__$a = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div')};
var __vue_staticRenderFns__$a = [];

  /* style */
  const __vue_inject_styles__$a = undefined;
  /* scoped */
  const __vue_scope_id__$a = undefined;
  /* module identifier */
  const __vue_module_identifier__$a = undefined;
  /* functional template */
  const __vue_is_functional_template__$a = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$a = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$a, staticRenderFns: __vue_staticRenderFns__$a },
    __vue_inject_styles__$a,
    __vue_script__$a,
    __vue_scope_id__$a,
    __vue_is_functional_template__$a,
    __vue_module_identifier__$a,
    false,
    undefined,
    undefined,
    undefined
  );

const getScaleControlOptions = (props) => {
    const { maxWidth, unit } = props;
    return {
        maxWidth,
        unit
    };
};
const mountScaleControl = async (vmb_map, vmb_scaleControl) => {
    const map = await vmb_map.promise;
    map.addControl(vmb_scaleControl);
};

var script$9 = defineComponent({
    name: 'MapboxScaleControl',
    props: {
        maxWidth: {
            type: Number,
            default: 100
        },
        unit: {
            type: String,
            default: 'metric'
        }
    },
    setup(props) {
        const options = getScaleControlOptions(props);
        const vmb_scaleControl = new mapboxgl.ScaleControl(options);
        const vmb_map = inject('vmb_map', null);
        onMounted(async () => {
            if (vmb_map)
                await mountScaleControl(vmb_map, vmb_scaleControl);
        });
        onUnmounted(async () => {
            if (vmb_map) {
                const map = await vmb_map.promise;
                map.removeControl(vmb_scaleControl);
            }
        });
    }
});

/* script */
const __vue_script__$9 = script$9;

/* template */
var __vue_render__$9 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div')};
var __vue_staticRenderFns__$9 = [];

  /* style */
  const __vue_inject_styles__$9 = undefined;
  /* scoped */
  const __vue_scope_id__$9 = undefined;
  /* module identifier */
  const __vue_module_identifier__$9 = undefined;
  /* functional template */
  const __vue_is_functional_template__$9 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$9 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$9, staticRenderFns: __vue_staticRenderFns__$9 },
    __vue_inject_styles__$9,
    __vue_script__$9,
    __vue_scope_id__$9,
    __vue_is_functional_template__$9,
    __vue_module_identifier__$9,
    false,
    undefined,
    undefined,
    undefined
  );

const getFullscreenControlOptions = (props) => filterObject(props);
const mountFullscreenControl = async (vmb_fullscreenControl, vmb_map) => {
    const map = await vmb_map.promise;
    map.addControl(vmb_fullscreenControl);
};

var script$8 = defineComponent({
    name: 'MapboxFullscreenControl',
    props: {
        container: {
            type: Object,
            default: undefined
        }
    },
    setup(props) {
        const vmb_map = inject('vmb_map', null);
        const options = getFullscreenControlOptions(props);
        const vmb_fullscreenControl = new mapboxgl.FullscreenControl(options);
        onMounted(async () => {
            if (vmb_map)
                await mountFullscreenControl(vmb_fullscreenControl, vmb_map);
        });
        onUnmounted(async () => {
            if (vmb_map) {
                const map = await vmb_map.promise;
                map.removeControl(vmb_fullscreenControl);
            }
        });
    }
});

/* script */
const __vue_script__$8 = script$8;

/* template */
var __vue_render__$8 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div')};
var __vue_staticRenderFns__$8 = [];

  /* style */
  const __vue_inject_styles__$8 = undefined;
  /* scoped */
  const __vue_scope_id__$8 = undefined;
  /* module identifier */
  const __vue_module_identifier__$8 = undefined;
  /* functional template */
  const __vue_is_functional_template__$8 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$8 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$8, staticRenderFns: __vue_staticRenderFns__$8 },
    __vue_inject_styles__$8,
    __vue_script__$8,
    __vue_scope_id__$8,
    __vue_is_functional_template__$8,
    __vue_module_identifier__$8,
    false,
    undefined,
    undefined,
    undefined
  );

var script$7 = defineComponent({
    name: 'MapboxSourceGeoJson',
    props: {
        container: {
            type: Object,
            default: undefined
        }
    },
    setup(props) {
        const vmb_map = inject('vmb_map', null);
        new GeoJSONSource();
        onMounted(async () => {
            if (vmb_map) {
                const map = await vmb_map.promise;
                map.addSource;
            }
        });
    }
});

/* script */
const __vue_script__$7 = script$7;

/* template */
var __vue_render__$7 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{ref:"features"},[_vm._t("default")],2)};
var __vue_staticRenderFns__$7 = [];

  /* style */
  const __vue_inject_styles__$7 = undefined;
  /* scoped */
  const __vue_scope_id__$7 = undefined;
  /* module identifier */
  const __vue_module_identifier__$7 = undefined;
  /* functional template */
  const __vue_is_functional_template__$7 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$7 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$7, staticRenderFns: __vue_staticRenderFns__$7 },
    __vue_inject_styles__$7,
    __vue_script__$7,
    __vue_scope_id__$7,
    __vue_is_functional_template__$7,
    __vue_module_identifier__$7,
    false,
    undefined,
    undefined,
    undefined
  );

const getGeolocationControlOptions = (props) => filterObject(props);

var script$6 = defineComponent({
    name: 'MapboxGeolocateControl',
    props: {
        positionOptions: {
            type: Object,
            default: () => ({ enableHighAccuracy: false, timeout: 6000 })
        },
        fitBoundsOptions: {
            type: Object,
            default: () => ({ maxZoom: 15 })
        },
        trackUserLocation: {
            type: Boolean,
            default: false
        },
        showAccuracyCircle: {
            type: Boolean,
            default: true
        },
        showUserLocation: {
            type: Boolean,
            default: true
        }
    },
    setup(props) {
        const vmb_map = inject('vmb_map', null);
        const options = getGeolocationControlOptions(props);
        const vmb_geolocationControl = new mapboxgl.GeolocateControl(options);
        onMounted(async () => {
            if (vmb_map) {
                const map = await vmb_map.promise;
                map.addControl(vmb_geolocationControl);
            }
        });
        onUnmounted(async () => {
            if (vmb_map) {
                const map = await vmb_map.promise;
                map.removeControl(vmb_geolocationControl);
            }
        });
    }
});

/* script */
const __vue_script__$6 = script$6;

/* template */
var __vue_render__$6 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div')};
var __vue_staticRenderFns__$6 = [];

  /* style */
  const __vue_inject_styles__$6 = undefined;
  /* scoped */
  const __vue_scope_id__$6 = undefined;
  /* module identifier */
  const __vue_module_identifier__$6 = undefined;
  /* functional template */
  const __vue_is_functional_template__$6 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$6 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$6, staticRenderFns: __vue_staticRenderFns__$6 },
    __vue_inject_styles__$6,
    __vue_script__$6,
    __vue_scope_id__$6,
    __vue_is_functional_template__$6,
    __vue_module_identifier__$6,
    false,
    undefined,
    undefined,
    undefined
  );

class Geogeometry {
    constructor(input) {
        this.id = input.id;
        this.fillColor = input.fillColor || '#4668F2';
        this.antialias = typeof input.antialias === 'boolean' ? input.antialias : true;
        this.opacity = typeof input.opacity === 'number' ? input.opacity : 0.6;
        if (input.outlineColor)
            this.outlineColor = input.outlineColor;
        this.deferred = new Deferred();
        provide('vmb_geogeometry', this.deferred);
    }
    updateOptions(input) {
        if (input.fillColor)
            this.fillColor = input.fillColor;
        if (typeof input.antialias === 'boolean')
            this.antialias = input.antialias;
        if (typeof input.opacity === 'number')
            this.opacity = input.opacity;
        if (input.outlineColor)
            this.outlineColor = input.outlineColor;
    }
    getPaint() {
        const paint = {};
        if (this.fillColor)
            paint['fill-color'] = this.fillColor;
        if (this.antialias)
            paint['fill-antialias'] = this.antialias;
        if (this.opacity)
            paint['fill-opacity'] = this.opacity;
        if (this.outlineColor)
            paint['fill-outline-color'] = this.outlineColor;
        return paint;
    }
    getLayer() {
        return {
            'id': this.id,
            'type': 'fill',
            'source': this.id,
            'layout': {},
            'paint': this.getPaint()
        };
    }
}

class Circle extends Geogeometry {
    constructor(input) {
        super(input);
        this.edges = input.edges || 10;
        this.radius = input.radius;
        this.center = input.center;
    }
    updateOptions(input) {
        super.updateOptions(input);
        if (input.edges)
            this.edges = input.edges;
        if (input.radius)
            this.radius = input.radius;
        if (input.center)
            this.center = input.center;
    }
    getGeoJSON() {
        const points = this.edges;
        const long = distanceToLong(this.radius, this.center[1]);
        const lat = distanceToLat(this.radius);
        const path = [];
        for (let i = 0; i < points; i++) {
            const theta = (i / points) * (2 * Math.PI);
            const x = long * Math.cos(theta);
            const y = lat * Math.sin(theta);
            path.push([this.center[0] + x, this.center[1] + y]);
        }
        path.push(path[0]);
        return {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [{
                        type: 'Feature',
                        geometry: {
                            type: 'Polygon',
                            coordinates: [path]
                        },
                        properties: {}
                    }],
            }
        };
    }
}

let circlesAdded = 0;
var script$5 = defineComponent({
    name: 'MapboxGeogeometryCircle',
    props: {
        id: {
            type: String,
            default: 'circle'
        },
        center: {
            type: Array,
            required: true
        },
        radius: {
            type: Number,
            required: true
        },
        edges: {
            type: Number,
            default: 10
        },
        fillColor: {
            type: String,
        },
        outlineColor: {
            type: String,
        },
        opacity: {
            type: Number
        },
        antialias: {
            type: Boolean
        }
    },
    setup(props) {
        const vmb_map = inject('vmb_map', null);
        const vmb_circle = new Deferred$1();
        const id = `${props.id}-${circlesAdded++}`;
        const circle = new Circle(filterObject({ ...props, id }, [
            'id',
            'radius',
            'center',
            'edges',
            'fillColor',
            'outlineColor',
            'opacity',
            'antialias'
        ]));
        provide('vmb_circle', vmb_circle);
        onMounted(async () => {
            if (vmb_map) {
                await mountGeogeometry(vmb_map, circle);
                vmb_circle.resolve(circle);
                circle.deferred.resolve(circle);
            }
        });
        watch(props, async () => {
            if (vmb_map) {
                await updateGeogeometry(props, vmb_map, circle);
            }
        });
        onUnmounted(async () => {
            if (vmb_map) {
                const map = await vmb_map.promise;
                map.removeLayer(circle.id);
            }
        });
    }
});

/* script */
const __vue_script__$5 = script$5;

/* template */
var __vue_render__$5 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{ref:"features"},[_vm._t("default",[_c('mapbox-geogeometry-fill',{attrs:{"color":_vm.fillColor,"outlineColor":_vm.outlineColor,"opacity":_vm.opacity,"antialias":_vm.antialias}},[_vm._t("popup")],2)])],2)};
var __vue_staticRenderFns__$5 = [];

  /* style */
  const __vue_inject_styles__$5 = undefined;
  /* scoped */
  const __vue_scope_id__$5 = undefined;
  /* module identifier */
  const __vue_module_identifier__$5 = undefined;
  /* functional template */
  const __vue_is_functional_template__$5 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5, staticRenderFns: __vue_staticRenderFns__$5 },
    __vue_inject_styles__$5,
    __vue_script__$5,
    __vue_scope_id__$5,
    __vue_is_functional_template__$5,
    __vue_module_identifier__$5,
    false,
    undefined,
    undefined,
    undefined
  );

class Polygon extends Geogeometry {
    constructor(input) {
        super(input);
        this.path = input.path;
    }
    updateOptions(input) {
        super.updateOptions(input);
        if (input.path)
            this.path = input.path;
    }
    get center() {
        const sum = this.path.reduce((acc, c) => [acc[0] + c[0], acc[1] + c[1]]);
        return [sum[0] / this.path.length, sum[1] / this.path.length];
    }
    getGeoJSON() {
        return {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [{
                        type: 'Feature',
                        geometry: {
                            type: 'Polygon',
                            coordinates: [this.path]
                        },
                        properties: {}
                    }],
            }
        };
    }
}

let polygonsAdded = 0;
var script$4 = defineComponent({
    name: 'MapboxGeogeometryPolygon',
    props: {
        id: {
            type: String,
            default: 'polygon'
        },
        path: {
            type: Array,
            required: true
        },
        fillColor: {
            type: String,
        },
        outlineColor: {
            type: String,
        },
        opacity: {
            type: Number
        },
        antialias: {
            type: Boolean
        }
    },
    setup(props) {
        const vmb_map = inject('vmb_map', null);
        const vmb_polygon = new Deferred$1();
        const id = `${props.id}-${polygonsAdded++}`;
        const polygon = new Polygon(filterObject({ ...props, id }, [
            'id',
            'path',
            'fillColor',
            'outlineColor',
            'opacity',
            'antialias'
        ]));
        provide('vmb_polygon', vmb_polygon);
        onMounted(async () => {
            if (vmb_map) {
                await mountGeogeometry(vmb_map, polygon);
                vmb_polygon.resolve(polygon);
                polygon.deferred.resolve(polygon);
            }
        });
        watch(props, async () => {
            if (vmb_map) {
                polygon.updateOptions(props);
                await updateGeogeometry(props, vmb_map, polygon);
            }
        });
        onUnmounted(async () => {
            if (vmb_map) {
                const map = await vmb_map.promise;
                map.removeLayer(polygon.id);
            }
        });
    }
});

/* script */
const __vue_script__$4 = script$4;

/* template */
var __vue_render__$4 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{ref:"features"},[_vm._t("default",[_c('mapbox-geogeometry-fill',{attrs:{"color":_vm.fillColor,"outlineColor":_vm.outlineColor,"opacity":_vm.opacity,"antialias":_vm.antialias}},[_vm._t("popup")],2)])],2)};
var __vue_staticRenderFns__$4 = [];

  /* style */
  const __vue_inject_styles__$4 = undefined;
  /* scoped */
  const __vue_scope_id__$4 = undefined;
  /* module identifier */
  const __vue_module_identifier__$4 = undefined;
  /* functional template */
  const __vue_is_functional_template__$4 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4, staticRenderFns: __vue_staticRenderFns__$4 },
    __vue_inject_styles__$4,
    __vue_script__$4,
    __vue_scope_id__$4,
    __vue_is_functional_template__$4,
    __vue_module_identifier__$4,
    false,
    undefined,
    undefined,
    undefined
  );

const rotateRectangle = (coords, angleDeg) => {
    if (coords.length !== 4)
        throw 'MapboxGeogeometryRectangle.rotateRectagle: Not a rectangle. Please supply exactly four coordinates';
    const angleRad = angleDeg * Math.PI / 180;
    const rotatedCoords = [];
    for (let i = 0; i < coords.length; i++) {
        rotatedCoords.push([
            coords[i][0] * Math.cos(angleRad) - coords[i][1] * Math.sin(angleRad),
            coords[i][0] * Math.sin(angleRad) + coords[i][1] * Math.cos(angleRad)
        ]);
    }
    return rotatedCoords;
};

class Rectangle extends Geogeometry {
    constructor(input) {
        super(input);
        this.width = input.width;
        this.height = input.height;
        this.center = input.center;
        this.rotationDeg = input.rotationDeg || 0;
    }
    updateOptions(input) {
        super.updateOptions(input);
        if (input.width)
            this.width = input.width;
        if (input.height)
            this.height = input.height;
        if (typeof input.rotationDeg === 'number' && input.rotationDeg !== this.rotationDeg)
            this.rotationDeg = input.rotationDeg;
    }
    get coordinates() {
        const xy = rotateRectangle([
            [this.width / 2, this.height / 2],
            [-this.width / 2, this.height / 2],
            [-this.width / 2, -this.height / 2],
            [this.width / 2, -this.height / 2]
        ], this.rotationDeg);
        const coords = [
            [this.center[0] + distanceToLong(xy[0][0], this.center[1]), this.center[1] + distanceToLat(xy[0][1])],
            [this.center[0] + distanceToLong(xy[1][0], this.center[1]), this.center[1] + distanceToLat(xy[1][1])],
            [this.center[0] + distanceToLong(xy[2][0], this.center[1]), this.center[1] + distanceToLat(xy[2][1])],
            [this.center[0] + distanceToLong(xy[3][0], this.center[1]), this.center[1] + distanceToLat(xy[3][1])],
        ];
        return coords;
    }
    getGeoJSON() {
        const path = [
            ...this.coordinates,
            this.coordinates[0]
        ];
        return {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [{
                        type: 'Feature',
                        geometry: {
                            type: 'Polygon',
                            coordinates: [path]
                        },
                        properties: {}
                    }],
            }
        };
    }
}

let rectanglesAdded = 0;
var script$3 = defineComponent({
    name: 'MapboxGeogeometryRectangle',
    props: {
        id: {
            type: String,
            default: 'rectangle'
        },
        center: {
            type: Array,
            required: true
        },
        width: {
            type: Number,
            required: true
        },
        height: {
            type: Number,
            required: true
        },
        fillColor: {
            type: String,
        },
        outlineColor: {
            type: String,
        },
        opacity: {
            type: Number,
            default: 0.6
        },
        antialias: {
            type: Boolean,
            default: true
        },
        rotationDeg: {
            type: Number
        }
    },
    setup(props) {
        const vmb_map = inject('vmb_map', null);
        const vmb_rectangle = new Deferred$1();
        const id = `${props.id}-${rectanglesAdded++}`;
        const rectangle = new Rectangle(filterObject({ ...props, id }, [
            'id',
            'width',
            'height',
            'center',
            'fillColor',
            'outlineColor',
            'opacity',
            'antialias',
            'rotationDeg'
        ]));
        provide('vmb_rectangle', vmb_rectangle);
        onMounted(async () => {
            if (vmb_map) {
                await mountGeogeometry(vmb_map, rectangle);
                vmb_rectangle.resolve(rectangle);
                rectangle.deferred.resolve(rectangle);
            }
        });
        onUnmounted(async () => {
            if (vmb_map) {
                const map = await vmb_map.promise;
                map.removeLayer(rectangle.id);
            }
        });
        watch(props, async () => {
            if (vmb_map) {
                await updateGeogeometry(props, vmb_map, rectangle);
            }
        });
    }
});

/* script */
const __vue_script__$3 = script$3;

/* template */
var __vue_render__$3 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{ref:"features"},[_vm._t("default",[_c('mapbox-geogeometry-fill',{attrs:{"color":_vm.fillColor,"outlineColor":_vm.outlineColor,"opacity":_vm.opacity,"antialias":_vm.antialias}},[_vm._t("popup")],2)])],2)};
var __vue_staticRenderFns__$3 = [];

  /* style */
  const __vue_inject_styles__$3 = undefined;
  /* scoped */
  const __vue_scope_id__$3 = undefined;
  /* module identifier */
  const __vue_module_identifier__$3 = undefined;
  /* functional template */
  const __vue_is_functional_template__$3 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3, staticRenderFns: __vue_staticRenderFns__$3 },
    __vue_inject_styles__$3,
    __vue_script__$3,
    __vue_scope_id__$3,
    __vue_is_functional_template__$3,
    __vue_module_identifier__$3,
    false,
    undefined,
    undefined,
    undefined
  );

const pathIsClosed = (pos) => {
    if (pos.length <= 1)
        return false;
    else {
        return pos[0][0] === pos[pos.length - 1][0] && pos[0][1] === pos[pos.length - 1][1];
    }
};
const inputIsPath = (potentialPath) => {
    if (potentialPath.length === 0)
        return true;
    if (potentialPath[0].length === 2 && typeof potentialPath[0][0] === 'number' && typeof potentialPath[0][1] === 'number')
        return true;
    return false;
};
const getCenterForCoordinates = (input, inputVisited = 0, inputCenter = [0, 0]) => {
    let visited = inputVisited;
    const center = [inputCenter[0], inputCenter[1]];
    if (inputIsPath(input)) {
        if (pathIsClosed(input))
            input.pop();
        for (const pos of input) {
            center[0] = (pos[0] + center[0] * visited) / (visited + 1);
            center[1] = (pos[1] + center[1] * visited) / (visited + 1);
            visited++;
        }
    }
    else {
        for (const pos of input) {
            const { visited: visitedLocal, center: centerLoacl } = getCenterForCoordinates(pos);
            center[0] = (center[0] * visited + centerLoacl[0] * visitedLocal) / (visitedLocal + visited);
            center[1] = (center[1] * visited + centerLoacl[1] * visitedLocal) / (visitedLocal + visited);
            visited += visitedLocal;
        }
    }
    return {
        visited,
        center
    };
};
const getCenterForFeature = (feature, inputVisited = 0, inputCenter = [0, 0]) => {
    let visited = inputVisited;
    const center = [inputCenter[0], inputCenter[1]];
    switch (feature.geometry.type) {
        case 'Polygon':
        case 'LineString': {
            const { center: centerLocal, visited: visitedLocal } = getCenterForCoordinates(feature.geometry.coordinates);
            center[0] = (center[0] * visited + centerLocal[0] * visitedLocal) / (visitedLocal + visited);
            center[1] = (center[1] * visited + centerLocal[1] * visitedLocal) / (visitedLocal + visited);
            visited += visitedLocal;
            break;
        }
    }
    return { visited, center };
};
const getCenterForFeatureCollection = (featureCollection, inputVisited = 0, inputCenter = [0, 0]) => {
    let visited = inputVisited;
    const center = [inputCenter[0], inputCenter[1]];
    for (let feature of featureCollection.features) {
        const { center: centerLocal, visited: visitedLocal } = getCenterForFeature(feature);
        center[0] = (center[0] * visited + centerLocal[0] * visitedLocal) / (visitedLocal + visited);
        center[1] = (center[1] * visited + centerLocal[1] * visitedLocal) / (visitedLocal + visited);
        visited += visitedLocal;
        break;
    }
    return { visited, center };
};

class Raw extends Geogeometry {
    constructor(input) {
        super(input);
        this.source = input.source;
    }
    updateOptions(input) {
        super.updateOptions(input);
        if (input.source)
            this.source = input.source;
    }
    get center() {
        const data = this.getGeoJSONData();
        if (!data || !data.type) {
            console.warn('Geogeometry Raw: Could not get center. Coordinates could not be obtained');
            return [0, 0];
        }
        if (data.type === 'Feature') {
            return getCenterForFeature(data).center;
        }
        if (data.type === 'FeatureCollection') {
            return getCenterForFeatureCollection(data).center;
        }
        throw new Error('Geogeometry Raw: Unknown Error getting Center');
    }
    getGeoJSON() {
        if (this.source.type === 'geojson')
            return this.source;
        else {
            return {
                type: 'geojson',
                data: this.source
            };
        }
    }
    getGeoJSONData() {
        return this.getGeoJSON().data;
    }
}

let rawDataAdded = 0;
var script$2 = defineComponent({
    name: 'MapboxGeogeometryRaw',
    props: {
        source: {
            type: Object,
            required: true,
        },
        id: {
            type: String,
            default: 'raw'
        },
        fillColor: {
            type: String,
            default: 'red'
        },
        outlineColor: {
            type: String,
        },
        opacity: {
            type: Number
        },
        antialias: {
            type: Boolean
        }
    },
    setup(props) {
        const vmb_map = inject('vmb_map', null);
        const vmb_raw = new Deferred();
        const id = `${props.id}-${rawDataAdded++}`;
        const raw = new Raw(filterObject({ ...props, id }, [
            'source',
            'id',
            'fillColor',
            'outlineColor',
            'opacity',
            'antialias'
        ]));
        provide('vmb_raw', vmb_raw);
        onMounted(async () => {
            if (vmb_map) {
                await mountGeogeometry(vmb_map, raw);
                vmb_raw.resolve(raw);
                raw.deferred.resolve(raw);
            }
        });
        watch(props, async () => {
            if (vmb_map) {
                raw.updateOptions(props);
                await updateGeogeometry(props, vmb_map, raw);
            }
        });
        onUnmounted(async () => {
            if (vmb_map) {
                const map = await vmb_map.promise;
                map.removeLayer(raw.id);
            }
        });
    }
});

/* script */
const __vue_script__$2 = script$2;

/* template */
var __vue_render__$2 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{ref:"features"},[_vm._t("default",[_c('mapbox-geogeometry-fill',{attrs:{"color":_vm.fillColor,"outlineColor":_vm.outlineColor,"opacity":_vm.opacity,"antialias":_vm.antialias}},[_vm._t("popup")],2)])],2)};
var __vue_staticRenderFns__$2 = [];

  /* style */
  const __vue_inject_styles__$2 = undefined;
  /* scoped */
  const __vue_scope_id__$2 = undefined;
  /* module identifier */
  const __vue_module_identifier__$2 = undefined;
  /* functional template */
  const __vue_is_functional_template__$2 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2, staticRenderFns: __vue_staticRenderFns__$2 },
    __vue_inject_styles__$2,
    __vue_script__$2,
    __vue_scope_id__$2,
    __vue_is_functional_template__$2,
    __vue_module_identifier__$2,
    false,
    undefined,
    undefined,
    undefined
  );

class GeogeometryPaint {
    constructor(input) {
        this.geogeometry = input.geogeometry;
        this.deferred = new Deferred();
        provide('vmb_geogeometry_paint', this.deferred);
    }
    async _init(paintType, id) {
        const geogeometry = await this.geogeometry.promise;
        this.geoId = geogeometry.id;
        this.id = `${this.geoId}-${paintType}-${id}`;
    }
    setGeoId(id) {
        this.geoId = id;
    }
}

let fillsAdded = 0;
class GeogeometryFill extends GeogeometryPaint {
    constructor(input) {
        super(input);
        this.geoId = input.geoId;
        this.id = `${input.geoId}-fill-${fillsAdded++}`;
        this.color = input.color || '#4668F2';
        this.antialias = typeof input.antialias === 'boolean' ? input.antialias : true;
        this.opacity = typeof input.opacity === 'number' ? input.opacity : 0.6;
        if (input.outlineColor)
            this.outlineColor = input.outlineColor;
    }
    async init() {
        await super._init('fill', fillsAdded++);
    }
    getPaint() {
        const paint = {};
        if (this.color)
            paint['fill-color'] = this.color;
        if (this.antialias)
            paint['fill-antialias'] = this.antialias;
        if (this.opacity)
            paint['fill-opacity'] = this.opacity;
        if (this.outlineColor)
            paint['fill-outline-color'] = this.outlineColor;
        return paint;
    }
    async update(props, vmb_map) {
        const map = await vmb_map.promise;
        const opts = filterObject(props);
        if (this.id) {
            if (opts.color && opts.color !== this.color) {
                map.setPaintProperty(this.id, 'fill-color', opts.color);
                this.color = opts.color;
            }
            if (typeof opts.antialias === 'boolean' && opts.antialias !== this.antialias) {
                map.setPaintProperty(this.id, 'fill-antialias', opts.antialias);
                this.antialias = opts.antialias;
            }
            if (typeof opts.opacity === 'number' && opts.opacity !== this.opacity) {
                map.setPaintProperty(this.id, 'fill-opacity', opts.opacity);
                this.opacity = opts.opacity;
            }
            if (opts.outlineColor && opts.outlineColor !== this.outlineColor) {
                map.setPaintProperty(this.id, 'fill-outline-color', opts.outlineColor);
                this.outlineColor = opts.outlineColor;
            }
        }
    }
    getLayer() {
        if (this.id) {
            return {
                id: this.id,
                type: 'fill',
                source: this.geoId,
                layout: {},
                paint: this.getPaint()
            };
        }
        else {
            throw new Error('Geogeometry.Paint.Fill: Cannot get layer. Not initialized');
        }
    }
}

var script$1 = defineComponent({
    name: 'MapboxGeogeometryFill',
    props: {
        color: {
            type: String,
            default: '#4668F2'
        },
        antialias: {
            type: Boolean,
            default: true
        },
        opacity: {
            type: Number,
            default: 0.6
        },
        outlineColor: {
            type: String
        }
    },
    setup(props) {
        const vmb_geogeometry = inject('vmb_geogeometry', null);
        const vmb_fill = new Deferred();
        const vmb_map = inject('vmb_map', null);
        if (!vmb_geogeometry)
            throw new Error('MapboxGeogeometryFill: no geogeometry found as parent');
        const fill = new GeogeometryFill({ ...filterObject(props), geogeometry: vmb_geogeometry });
        provide('vmb_fill', vmb_fill);
        onMounted(async () => {
            if (vmb_map && vmb_geogeometry) {
                const map = await vmb_map.promise;
                const geogeometry = await vmb_geogeometry.promise;
                if (geogeometry && map) {
                    await fill.init();
                    map.addLayer(fill.getLayer());
                    vmb_fill.resolve(fill);
                    fill.deferred.resolve(fill);
                }
            }
        });
        watch(props, async () => {
            const fill = await vmb_fill.promise;
            if (fill && vmb_map)
                fill.update(props, vmb_map);
        });
        onUnmounted(async () => {
            if (vmb_map && fill.id) {
                const map = await vmb_map.promise;
                map.removeLayer(fill.id);
            }
        });
        return {};
    }
});

/* script */
const __vue_script__$1 = script$1;

/* template */
var __vue_render__$1 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[_vm._t("default")],2)};
var __vue_staticRenderFns__$1 = [];

  /* style */
  const __vue_inject_styles__$1 = undefined;
  /* scoped */
  const __vue_scope_id__$1 = undefined;
  /* module identifier */
  const __vue_module_identifier__$1 = undefined;
  /* functional template */
  const __vue_is_functional_template__$1 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1, staticRenderFns: __vue_staticRenderFns__$1 },
    __vue_inject_styles__$1,
    __vue_script__$1,
    __vue_scope_id__$1,
    __vue_is_functional_template__$1,
    __vue_module_identifier__$1,
    false,
    undefined,
    undefined,
    undefined
  );

let linesAdded = 0;
class GeogeometryLine extends GeogeometryPaint {
    constructor(input) {
        super(input);
        this.color = input.color || '#000';
        this.opacity = typeof input.opacity === 'number' ? input.opacity : 0.6;
        if (input.geoId) {
            this.geoId = input.geoId;
        }
        if (typeof input.blur === 'number')
            this.blur = input.blur;
        if (input.cap)
            this.cap = input.cap;
        if (input.join)
            this.join = input.join;
        if (typeof input.width === 'number')
            this.width = input.width;
        if (input.translate)
            this.translate = input.translate;
        if (typeof input.offset === 'number')
            this.offset = input.offset;
        if (input.dasharray)
            this.dasharray = input.dasharray;
        if (typeof input.gapWidth === 'number')
            this.gapWidth = input.gapWidth;
        if (input.gradient)
            this.gradient = input.gradient;
        if (typeof input.miterLimit === 'number')
            this.miterLimit = input.miterLimit;
        if (typeof input.roundLimit === 'number')
            this.roundLimit = input.roundLimit;
        if (typeof input.sortKey === 'number')
            this.sortKey = input.sortKey;
        if (input.translateAnchor)
            this.translateAnchor = input.translateAnchor;
    }
    getPaint() {
        const paintRaw = {
            'line-blur': this.blur,
            'line-cap': this.cap,
            'line-join': this.join,
            'line-opacity': this.join,
            'line-color': this.color,
            'line-width': this.width,
            'line-translate': this.translate,
            'line-offset': this.offset,
            'line-dasharray': this.dasharray,
            'line-gap-width': this.gapWidth,
            // 'line-gradient': this.gradient,
            'line-miter-limit': this.miterLimit,
            'line-round-limimt': this.roundLimit,
            'line-sort-key': this.sortKey,
            'line-translate-anchor': this.translateAnchor,
        };
        const paint = filterObject(paintRaw);
        return paint;
    }
    init() {
        super._init('line', linesAdded++);
        return this;
    }
    async update(props, vmb_map) {
        const map = await vmb_map.promise;
        const opts = filterObject(props);
        if (this.id) {
            if (opts.color && opts.color !== this.color) {
                map.setPaintProperty(this.id, 'line-color', opts.color);
                this.color = opts.color;
            }
            if (typeof opts.blur === 'number' && opts.blur !== this.blur) {
                map.setPaintProperty(this.id, 'line-blur', opts.blur);
                this.blur = opts.blur;
            }
            if (opts.cap && opts.cap !== this.cap) {
                map.setPaintProperty(this.id, 'line-cap', opts.cap);
                this.cap = opts.cap;
            }
            if (opts.join && opts.join !== this.join) {
                map.setPaintProperty(this.id, 'line-join', opts.join);
                this.join = opts.join;
            }
            if (typeof opts.opacity === 'number' && opts.opacity !== this.opacity) {
                map.setPaintProperty(this.id, 'line-opacity', opts.opacity);
                this.opacity = opts.opacity;
            }
            if (typeof opts.width === 'number' && opts.width !== this.width) {
                map.setPaintProperty(this.id, 'line-width', opts.width);
                this.width = opts.width;
            }
            if (opts.translate && this.translate && (opts.translate[0] !== this.translate[0] || opts.translate[1] !== this.translate[1])) {
                map.setPaintProperty(this.id, 'line-translate', opts.translate);
                this.translate = opts.translate;
            }
            if (typeof opts.offset === 'number' && this.offset !== opts.offset) {
                map.setPaintProperty(this.id, 'line-offset', opts.offset);
                this.offset = opts.offset;
            }
            if (opts.dasharray && !deepEqual(this.dasharray, opts.dasharray)) {
                map.setPaintProperty(this.id, 'line-dasharray', opts.dasharray);
                this.dasharray = opts.dasharray;
            }
            if (typeof opts.gapWidth === 'number' && this.gapWidth !== opts.gapWidth) {
                map.setPaintProperty(this.id, 'line-gap-width', opts.gapWidth);
                this.gapWidth = opts.gapWidth;
            }
            // if(opts.gradient && this.gradient !== opts.gradient){
            //   map.setPaintProperty(this.id, 'line-gradient', opts.gradient);
            //   this.gradient = opts.gradient;
            // }
            if (typeof opts.miterLimit === 'number' && this.miterLimit !== opts.miterLimit) {
                map.setPaintProperty(this.id, 'line-miter-limit', opts.miterLimit);
                this.miterLimit = opts.miterLimit;
            }
            if (typeof opts.roundLimit === 'number' && this.roundLimit !== opts.roundLimit) {
                map.setPaintProperty(this.id, 'line-round-limit', opts.roundLimit);
                this.roundLimit = opts.roundLimit;
            }
            if (typeof opts.sortKey === 'number') {
                map.setPaintProperty(this.id, 'line-sort-key', opts.sortKey);
                this.sortKey = opts.sortKey;
            }
            if (opts.translateAnchor && this.translateAnchor !== opts.translateAnchor) {
                map.setPaintProperty(this.id, 'line-translate-anchor', opts.translateAnchor);
                this.translateAnchor = opts.translateAnchor;
            }
        }
    }
    getLayer() {
        if (this.id) {
            return {
                id: this.id,
                type: 'line',
                source: this.geoId,
                layout: {},
                paint: this.getPaint()
            };
        }
        else {
            throw new Error('Geogeometry.Paint.Line: Cannot get Layer. Not initialized');
        }
    }
}

var script = defineComponent({
    name: 'MapboxGeogeometryLine',
    props: {
        blur: {
            type: Number
        },
        cap: {
            type: String
        },
        join: {
            type: String
        },
        opacity: {
            type: Number,
            default: 1
        },
        color: {
            type: String,
            default: '#4668F2'
        },
        width: {
            type: Number
        },
        translate: {
            type: Array
        },
        offset: {
            type: Number
        },
        dasharray: {
            type: Array
        },
        gapWidth: {
            type: Number
        },
        // gradient: {
        //   type: String
        // },
        // miterLimit: {
        //   type: Number
        // },
        // roundLimit: {
        //   type: Number
        // },
        // sortKey: {
        //   type: Number
        // },
        // translateAnchor: {
        //   type: String as () => TranslateAnchor
        // }
    },
    setup(props) {
        const vmb_geogeometry = inject('vmb_geogeometry', null);
        const vmb_line = new Deferred();
        const vmb_map = inject('vmb_map', null);
        provide('vmb_paint', vmb_line);
        if (!vmb_geogeometry)
            throw new Error('MapboxGeogeometryLine: no geogeometry found as parent');
        const line = new GeogeometryLine({ ...filterObject(props), geogeometry: vmb_geogeometry });
        onMounted(async () => {
            if (vmb_map && vmb_geogeometry) {
                await line.init();
                const map = await vmb_map.promise;
                const geogeometry = await vmb_geogeometry.promise;
                if (geogeometry && map) {
                    map.addLayer(line.getLayer());
                    vmb_line.resolve(line);
                }
            }
        });
        watch(props, async () => {
            const line = await vmb_line.promise;
            if (line && vmb_map)
                line.update(props, vmb_map);
        });
        onUnmounted(async () => {
            if (vmb_map && line.id) {
                const map = await vmb_map.promise;
                map.removeLayer(line.id);
            }
        });
        return {};
    }
});

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div')};
var __vue_staticRenderFns__ = [];

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

// export default {
//   // MapboxGeocoder,
//   // MapboxImage,
//   // MapboxImages,
//   // MapboxLayer,
//   MapboxMap,
//   MapboxMarker,
//   // MapboxNavigationControl,
//   // MapboxPopup,
//   // MapboxSource,
//   // MapboxGeolocateControl,
//   // TestComponentInject,
//   // TestComponentProvide
// };

var components = /*#__PURE__*/Object.freeze({
  __proto__: null,
  MapboxGeocoderControl: __vue_component__$f,
  MapboxMap: __vue_component__$e,
  MapboxMarker: __vue_component__$d,
  MapboxNavigationControl: __vue_component__$c,
  MapboxPopup: __vue_component__$b,
  MapboxAttributionControl: __vue_component__$a,
  MapboxScaleControl: __vue_component__$9,
  MapboxFullscreenControl: __vue_component__$8,
  MapboxSourceGeoJson: __vue_component__$7,
  MapboxGeolocateControl: __vue_component__$6,
  MapboxGeogeometryCircle: __vue_component__$5,
  MapboxGeogeometryPolygon: __vue_component__$4,
  MapboxGeogeometryRectangle: __vue_component__$3,
  MapboxGeogeometryRaw: __vue_component__$2,
  MapboxGeogeometryFill: __vue_component__$1,
  MapboxGeogeometryLine: __vue_component__
});

var install = (instance) => {
    instance.use(compositionApi);
    Object.keys(components).forEach((name) => {
        instance.component(name, components[name]);
    });
};

class Animation {
    constructor(data, renderFunc, opts = { speed: 1 }) {
        this.renderFunc = renderFunc;
        this.running = false;
        this.progress = 0;
        this.data = data;
        this.startTime = performance.now();
        this.speed = opts.speed;
        this.pauseTime = 0;
    }
    start() {
        this.startTime = performance.now() - this.progress;
        this.internalRenderFunc(this.progress);
    }
    pause() {
        this.pauseTime += performance.now() - this.pauseTime;
        if (this.animation)
            cancelAnimationFrame(this.animation);
    }
    reset() {
        this.startTime = performance.now();
        this.progress = 0;
    }
    stop() {
        this.pause();
        this.reset();
        this.renderFunc(this.progress, this.data);
    }
    internalRenderFunc(timestamp) {
        this.progress = (timestamp - this.startTime) * this.speed;
        this.renderFunc(this.progress, this.data);
        this.animation = requestAnimationFrame(this.internalRenderFunc.bind(this));
    }
}

export default install;
export { Animation, __vue_component__$a as MapboxAttributionControl, __vue_component__$8 as MapboxFullscreenControl, __vue_component__$f as MapboxGeocoderControl, __vue_component__$5 as MapboxGeogeometryCircle, __vue_component__$1 as MapboxGeogeometryFill, __vue_component__ as MapboxGeogeometryLine, __vue_component__$4 as MapboxGeogeometryPolygon, __vue_component__$2 as MapboxGeogeometryRaw, __vue_component__$3 as MapboxGeogeometryRectangle, __vue_component__$6 as MapboxGeolocateControl, __vue_component__$e as MapboxMap, __vue_component__$d as MapboxMarker, __vue_component__$c as MapboxNavigationControl, __vue_component__$b as MapboxPopup, __vue_component__$9 as MapboxScaleControl, __vue_component__$7 as MapboxSourceGeoJson };
